/*
 * PRD-037 — Wizard UNIFICADO "Preparar comercio" (app SEPARADA del partner).
 *
 * UN solo flujo que pre-llena TODA la preparación del comercio sobre el borrador para que el alta
 * del dueño sea de <1 min (solo confirmar). Antes eran flujos sueltos (Configurar + Cargar menú +
 * Invitar) que "parecían páginas y procesos separados"; ahora son los 7 pasos de un único WizardV4
 * con UNA sola sesión-draft (se abre una vez en `useDraftSession`, no por paso):
 *
 *   [1 General] [2 Tipo] [3 Dirección] [4 Entrega] [5 Horarios] [6 Menú] [7 Invitar]
 *
 * Estrategia de reuso (mismo patrón que el panel OnBoarding.tsx): la sesión-draft deja el JWT del
 * borrador en localStorage como token de comercio, así el GET/PUT `/v1/comercio`, el SmartMenu
 * (`/v1/categoria`, `/v1/producto`) y demás endpoints del panel quedan scoped al borrador. Los pasos
 * son componentes prop-driven alimentados por `useDraftCommerce` (estado SIN Redux). El paso de menú
 * monta `<SmartMenuOnboarding>` plegado (como OnBoarding.tsx) y el de invitar genera el enlace de
 * activación con `createClaimLink` (cliente partner dedicado, independiente del token-draft).
 *
 * NO se setea `onboarding_finish_date`: eso lo sigue escribiendo el wizard del panel cuando el dueño
 * pulsa "Finalizar" en su configuración inicial (se respeta el PRD).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { WizardV4, type WizardV4Step, type WizardV4Handle } from '@components/v4/Wizard';
import BusinessTypeV4 from '@pages/OnBoarding/steps/BusinessTypeV4';
import ScheduleManager from '@components/schedule/ScheduleManager';
import ScheduleManagerCompact from '@components/schedule/ScheduleManagerCompact';
import { useDesktop } from '@utils/hooks/useDesktop';
import { validateBusinessTypeValues, validateCommerce } from '@pages/OnBoarding/utils/validators';
import {
  SmartMenuOnboarding,
  useHasExistingCatalog,
  type SmartMenuCompletionResult,
} from '@components/v4/onboarding/SmartMenuOnboarding';
import { useFetchCallback } from '@services/services';
import { createClaimLink } from '@services/partnerService';
import type { Category, Product } from '@interfaces/general';
import { TextInputV4 } from '@components/v4/Input/Input';
import { usePartnerAuth } from '../PartnerAuthContext';
import { useDraftSession } from '../useDraftSession';
import { useDraftCommerce } from '../useDraftCommerce';
import { ConfigureGeneralSection } from '../ConfigureGeneralSection';
import { ConfigureAddressSection } from '../ConfigureAddressSection';
import { ConfigureDeliverySection } from '../ConfigureDeliverySection';
import { Button, C, Card, ErrorNote, Muted, PageShell, Title } from '../ui';

// Identificadores de paso → permiten deep-link a un paso concreto vía `?step=` (p. ej. el atajo
// "Cargar menú" abre el wizard directamente en el paso de menú) sin romper el orden del flujo.
const STEP_IDS = ['general', 'tipo', 'direccion', 'entrega', 'horarios', 'menu', 'invitar'] as const;
export type StepId = (typeof STEP_IDS)[number];

// Catálogo EXISTENTE del borrador (categorías + productos) traído por axios directo — la app del
// partner NO monta Redux, así que NO se pueden usar useGetCategories/useGetProducts (acoplados al
// store). Solo se dispara cuando ya hay catálogo (`enabled`), para alimentar el matching WS-B del
// SmartMenu en su fase 'review-existing'.
const useDraftCatalog = (enabled: boolean) => {
  const { call: fetchCategories } = useFetchCallback<Category[]>('get', '/v1/categoria');
  const { call: fetchProducts } = useFetchCallback<Product[]>('get', '/v1/producto');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    Promise.all([fetchCategories(), fetchProducts()])
      .then(([cats, prods]) => {
        if (!active) return;
        setCategories(cats || []);
        setProducts(prods || []);
      })
      .catch(() => {
        /* el SmartMenu degrada a crear todo si no llega el catálogo existente */
      });
    return () => {
      active = false;
    };
  }, [enabled, fetchCategories, fetchProducts]);

  return { categories, products };
};

// Paso [6 Menú]: monta el SmartMenu sobre la sesión-draft (igual que OnBoarding.tsx). Si el partner
// ya precargó productos, arranca en 'review-existing' mostrándolos como ya procesados; si no, en el
// uploader. Solo monta cuando es el paso activo (WizardV4 renderiza únicamente el componente del
// paso actual), así la verificación de catálogo no corre hasta que el partner llega al menú.
const MenuStep = ({ onDone }: { onDone: (result: SmartMenuCompletionResult) => void }) => {
  const { hasCatalog } = useHasExistingCatalog();
  const { categories, products } = useDraftCatalog(hasCatalog === true);

  if (hasCatalog === null) {
    return <Muted>Verificando si el comercio ya tiene menú cargado...</Muted>;
  }

  return (
    <SmartMenuOnboarding
      skippable
      trackingSource="cargar_menu"
      alreadyHasCatalog={hasCatalog === true}
      existingCategories={hasCatalog === true ? categories : undefined}
      existingProducts={hasCatalog === true ? products : undefined}
      onComplete={onDone}
    />
  );
};

// Paso [7 Invitar]: genera/copia el enlace de activación (misma lógica inline que CommerceDetail).
// `createClaimLink` usa el cliente `partnerApi` dedicado (header `partner-token`), así que funciona
// dentro de la sesión-draft sin tocar el token-draft de localStorage.
// Paso secuencial (una primaria a la vez):
//  - Estado A (sin enlace): única primaria "Generar enlace de activación". El "Finalizar" del wizard
//    está oculto (hideNextButton) para no competir con esta acción.
//  - Estado B (enlace generado): enlace + copiar / compartir por WhatsApp y la primaria "Finalizar"
//    (cierra el wizard vía `onFinish` → vuelve al detalle). El "Anterior" del wizard se mantiene.
const InviteStep = ({ idComercio, onFinish }: { idComercio: string; onFinish: () => void }) => {
  const { token } = usePartnerAuth();
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!token) return;
    setError(null);
    setCopied(false);
    setLoading(true);
    try {
      const res = await createClaimLink(token, idComercio);
      setLink(`${window.location.origin}/pre-registro/${res.token}`);
    } catch {
      setError('No se pudo generar el enlace de activación.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* sin clipboard (contexto inseguro): el dueño puede seleccionar y copiar manualmente */
    }
  };

  // Abre WhatsApp (app o web) con el enlace pre-cargado en el mensaje para que el partner
  // se lo reenvíe al dueño. `wa.me` sin número deja al partner elegir el destinatario.
  const shareWhatsApp = () => {
    if (!link) return;
    const text = `Activa tu comercio en Plick con este enlace (válido 72h): ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
        Genera el enlace de activación y compártelo con el dueño por WhatsApp. Al abrirlo validará su teléfono con un
        código y activará su cuenta (válido 72h).
      </p>
      {!link ? (
        <Button onClick={generate} loading={loading} disabled={loading || !token}>
          {loading ? 'Generando...' : 'Generar enlace de activación'}
        </Button>
      ) : (
        <>
          <TextInputV4
            value={link}
            onChange={() => {}}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Enlace de activación"
          />
          {/* Acciones secundarias sobre el enlace — ninguna es la primaria del paso. */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={copy}>
              {copied ? 'Copiado ✓' : 'Copiar enlace'}
            </Button>
            <Button variant="secondary" onClick={shareWhatsApp}>
              Compartir por WhatsApp
            </Button>
            <Button variant="ghost" onClick={generate} disabled={loading}>
              Generar otro
            </Button>
          </div>
          {/* Única primaria del paso: cierra el wizard (sustituye al "Finalizar" oculto del wizard). */}
          <Button variant="primary" size="lg" fullWidth onClick={onFinish}>
            Finalizar
          </Button>
        </>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
};

// Carga el borrador y monta el wizard. Se renderiza SOLO cuando la sesión-draft ya está lista
// (token en localStorage), así el GET/PUT /v1/comercio quedan scoped al borrador.
const ConfigureInner = ({ idComercio, initialStep }: { idComercio: string; initialStep: number }) => {
  const navigate = useNavigate();
  const isDesktop = useDesktop();
  const { commerce, loading, saving, loadError, sessionExpired, handleUpdateValue, save } = useDraftCommerce();
  const [saveError, setSaveError] = useState<string | null>(null);
  // Handle imperativo del wizard: el paso de menú NO muestra "Siguiente" (sería un
  // segundo primario compitiendo con "Publicar mi menú" del SmartMenu); en su lugar,
  // cuando el SmartMenu notifica fin (publicó o saltó) avanzamos a Invitar por aquí.
  const wizardRef = useRef<WizardV4Handle>(null);

  const backToDetail = () => navigate(`/partner/comercios/${idComercio}`);

  // Cierre del wizard: lo comparten el `onFinish` del WizardV4 y la primaria "Finalizar" que el
  // paso Invitar renderiza por su cuenta (el "Finalizar" del wizard queda oculto vía hideNextButton).
  const finishWizard = useCallback(
    () => navigate(`/partner/comercios/${idComercio}?prepared=1`),
    [navigate, idComercio],
  );

  // Persiste el borrador. En error fija el mensaje y RE-LANZA para que el wizard no avance de paso
  // (mismo contrato que handleSubmitChanges del panel).
  const persist = useCallback(async () => {
    try {
      await save();
      setSaveError(null);
    } catch (err) {
      setSaveError('No se pudo guardar la configuración. Revisa los datos e intenta de nuevo.');
      throw err;
    }
  }, [save]);

  // El SmartMenu persiste su propio resultado server-side; en el wizard es informativo (el paso de
  // menú no muta el Commerce, así que no requiere PUT al avanzar). El SmartMenu es un sub-flujo
  // autocontenido con sus propias CTAs ("Publicar mi menú" / "Guardar y terminar después"); su
  // `onComplete` es el ÚNICO disparador de avance del paso. Avanzamos a Invitar haya publicado o
  // saltado el menú (es opcional/best-effort) — evitando un segundo "Siguiente" del wizard que
  // competía con el primario del SmartMenu.
  const onMenuDone = useCallback(() => {
    wizardRef.current?.goNext();
  }, []);

  // Sesion-draft expirada (401): mensaje especifico — NO el generico de loadError/save. El token ya
  // se limpio en el handler de 401; el dueno debe reabrir el borrador desde el detalle.
  if (sessionExpired) {
    return (
      <Card>
        <Title>Tu sesión del borrador expiró</Title>
        <Muted>Vuelve al detalle del comercio y abre de nuevo &quot;Preparar comercio&quot; para continuar.</Muted>
        <Button variant="secondary" onClick={backToDetail} style={{ marginTop: 14 }}>
          Volver al comercio
        </Button>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <Title>No se pudo abrir el borrador</Title>
        <Muted>Intenta de nuevo o vuelve al detalle del comercio.</Muted>
        <Button variant="secondary" onClick={backToDetail} style={{ marginTop: 14 }}>
          Volver al comercio
        </Button>
      </Card>
    );
  }

  if (loading || !commerce) {
    return (
      <Card>
        <Title>Cargando configuración...</Title>
        <Muted>Abriendo el borrador del comercio.</Muted>
      </Card>
    );
  }

  const generalMissing = validateCommerce(commerce);
  const businessMissing = validateBusinessTypeValues(commerce);

  const steps: WizardV4Step[] = [
    {
      label: 'General',
      component: (
        <ConfigureGeneralSection
          commerce={commerce}
          missingValues={generalMissing}
          handleUpdateValue={handleUpdateValue}
        />
      ),
      onSubmit: persist,
      disableNextButton: generalMissing.length > 0 || saving,
    },
    {
      label: 'Tipo de negocio',
      component: <BusinessTypeV4 commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: businessMissing.length > 0 || saving,
    },
    {
      label: 'Dirección',
      component: <ConfigureAddressSection commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: saving,
    },
    {
      label: 'Entrega',
      component: <ConfigureDeliverySection commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: saving,
    },
    {
      label: 'Horarios',
      // ScheduleManager es una tabla densa del panel. En móvil usamos la variante compacta
      // (acota la altura con scroll interno para no empujar el botón "Siguiente" fuera de vista)
      // y los inputs ya se encogen para caber en el ancho del card del wizard (responsive interno).
      component: (
        <div style={{ width: '100%', maxWidth: '100%' }}>
          {isDesktop ? (
            <ScheduleManager
              autoSave
              schedules={commerce.horarios || []}
              onChange={(s) => handleUpdateValue('horarios', s)}
              customTitle="Horarios de atención:"
            />
          ) : (
            <ScheduleManagerCompact
              autoSave
              schedules={commerce.horarios || []}
              onChange={(s) => handleUpdateValue('horarios', s)}
              customTitle="Horarios de atención:"
            />
          )}
        </div>
      ),
      onSubmit: persist,
      disableNextButton: saving,
    },
    {
      label: 'Menú',
      // [6 Menú]: paso best-effort. Mismo criterio de jerarquía de CTAs que el OnBoarding del panel:
      // se OCULTA el "Siguiente" del wizard (`hideNextButton`) para no tener dos primarios compitiendo
      // ("Publicar mi menú" del SmartMenu vs "Siguiente" del wizard). El SmartMenu es la única CTA
      // primaria; su "Guardar y terminar después" es el omitir claro. Al notificar `onComplete`
      // (publicó o saltó) avanzamos a Invitar vía `wizardRef.goNext()` (ver `onMenuDone`). "Anterior"
      // del wizard se mantiene (no depende de `hideNextButton`).
      component: <MenuStep onDone={onMenuDone} />,
      hideNextButton: true,
    },
    {
      label: 'Invitar',
      // Último paso. Mismo criterio anti doble-CTA: se OCULTA el "Finalizar" del wizard
      // (hideNextButton también cubre el botón Finalizar del último paso) para no competir con la
      // acción del paso. En estado A la única primaria es "Generar enlace"; en estado B el propio
      // InviteStep muestra "Finalizar" (→ finishWizard). "Anterior" del wizard se mantiene.
      component: <InviteStep idComercio={idComercio} onFinish={finishWizard} />,
      hideNextButton: true,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <Button variant="ghost" onClick={backToDetail}>
          ← Volver al comercio
        </Button>
      </div>
      <WizardV4
        ref={wizardRef}
        title="Preparar comercio"
        subtitle="Pre-llena la configuración del negocio paso a paso. Entre más prepares, más rápido será el alta del dueño (solo confirmar)."
        isLoading={saving}
        steps={steps}
        initialStep={initialStep}
        // Cada paso ya persiste al avanzar (Siguiente). El paso de menú avanza por su cuenta vía
        // `wizardRef.goNext()` cuando el SmartMenu termina; el paso Invitar cierra el flujo con su
        // propia primaria "Finalizar" (→ finishWizard). Se conserva `onFinish` por contrato del
        // WizardV4 (lo invocaría su "Finalizar" si no estuviera oculto).
        onFinish={finishWizard}
      />
      {saveError && <ErrorNote>{saveError}</ErrorNote>}
    </>
  );
};

const ConfigureCommerce = ({ initialStepId }: { initialStepId?: StepId }) => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, error } = useDraftSession(token, id);

  // El paso inicial sale del `?step=` (deep-link, p. ej. el atajo "Cargar menú") o del prop de la
  // ruta; si ninguno aplica, arranca en General (0). WizardV4 lo clampa al rango válido.
  const requestedStep = (searchParams.get('step') as StepId | null) || initialStepId;
  const initialStep = requestedStep ? Math.max(0, STEP_IDS.indexOf(requestedStep)) : 0;

  if (error) {
    return (
      <PageShell>
        <Card>
          <Title>Preparar comercio</Title>
          <ErrorNote>{error}</ErrorNote>
          <Button variant="secondary" onClick={() => navigate('/partner')} style={{ marginTop: 14 }}>
            Volver al portal
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (!ready || !id) {
    return (
      <PageShell>
        <Card>
          <Title>Preparando configuración...</Title>
          <Muted>Abriendo la sesión del borrador.</Muted>
        </Card>
      </PageShell>
    );
  }

  // El ThemeProvider que necesitan ScheduleManager/SmartMenu ya se monta en el root (PartnerApp).
  return (
    <PageShell>
      <ConfigureInner idComercio={id} initialStep={initialStep} />
    </PageShell>
  );
};

export default ConfigureCommerce;
